import React, { useState, useEffect } from "react";

export interface RatingProps {
	initialValue?: number;
	size?: number;
	readonly?: boolean;
	fillColor?: string;
	emptyColor?: string;
	style?: React.CSSProperties;
	disableFillHover?: boolean;
	onChange?: (rating: number) => void;
	className?: string;
}

const Rating: React.FC<RatingProps> = ({
	initialValue = 0,
	size = 24,
	readonly = false,
	fillColor = "#ffc107",
	emptyColor = "#e4e5e9",
	style,
	disableFillHover = false,
	onChange,
	className = "",
}) => {
	const [rating, setRating] = useState(initialValue);
	const [hoverRating, setHoverRating] = useState(0);

	useEffect(() => {
		setRating(initialValue);
	}, [initialValue]);

	const handleClick = (starIndex: number) => {
		if (readonly) return;

		const newRating = starIndex + 1;
		setRating(newRating);
		onChange?.(newRating);
	};

	const handleMouseEnter = (starIndex: number) => {
		if (readonly || disableFillHover) return;
		setHoverRating(starIndex + 1);
	};

	const handleMouseLeave = () => {
		if (readonly || disableFillHover) return;
		setHoverRating(0);
	};

	const getStarFill = (starIndex: number) => {
		const currentRating = hoverRating || rating;
		const starValue = starIndex + 1;

		if (currentRating >= starValue) {
			return fillColor;
		} else if (currentRating > starIndex && currentRating < starValue) {
			// Partial fill for decimal ratings
			const percentage = (currentRating - starIndex) * 100;
			return `url(#gradient-${starIndex})`;
		}
		return emptyColor;
	};

	const renderGradientDefs = () => (
		<defs>
			{[0, 1, 2, 3, 4].map((index) => {
				const currentRating = hoverRating || rating;
				const starValue = index + 1;
				const percentage =
					currentRating > index && currentRating < starValue ? (currentRating - index) * 100 : 0;

				return (
					<linearGradient key={index} id={`gradient-${index}`}>
						<stop offset={`${percentage}%`} stopColor={fillColor} />
						<stop offset={`${percentage}%`} stopColor={emptyColor} />
					</linearGradient>
				);
			})}
		</defs>
	);

	return (
		<div className={`inline-flex items-center ${className}`} style={style}>
			<svg width="0" height="0">
				{renderGradientDefs()}
			</svg>
			{[0, 1, 2, 3, 4].map((starIndex) => (
				<svg
					key={starIndex}
					width={size}
					height={size}
					viewBox="0 0 24 24"
					fill={getStarFill(starIndex)}
					className={`${!readonly ? "cursor-pointer" : ""} transition-colors duration-150`}
					onClick={() => handleClick(starIndex)}
					onMouseEnter={() => handleMouseEnter(starIndex)}
					onMouseLeave={handleMouseLeave}
				>
					<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
				</svg>
			))}
		</div>
	);
};

export default Rating;
